import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  Sparkles,
  Award,
  Heart,
  Quote,
  MapPin,
  ArrowRight,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────────
 * Tested, Tasted, and Loved — Influencer / Brand Partner carousel
 *
 * Premium social-proof strip surfaced on Landing & How It Works.
 * Placeholder data; real partners will be swapped in later. Cards use
 * horizontal scroll-snap on all screen sizes with arrow controls on
 * desktop. Compliance: copy avoids medical/health claims and section
 * exposes badge slots so paid/gifted/sponsored relationships can be
 * disclosed transparently.
 * ──────────────────────────────────────────────────────────────────────── */

const BADGE_STYLES = {
  "Approved Partner": {
    icon: BadgeCheck,
    bg: "rgba(255,255,255,0.35)",
    fg: "var(--ff-primary)",
  },
  "Taste Tested": {
    icon: Sparkles,
    bg: "rgba(255,255,255,0.35)",
    fg: "#B5651A",
  },
  "Brand Ambassador": {
    icon: Award,
    bg: "rgba(255,255,255,0.35)",
    fg: "#F4A261",
  },
  "Farm To You Favorite": {
    icon: Heart,
    bg: "rgba(255,255,255,0.35)",
    fg: "var(--ff-primary)",
  },
};

// Real influencer roster.
const PARTNERS = [
  {
    id: "julian-decina",
    name: "Julian Decina",
    title: "Bodybuilder & Fitness Creator",
    location: "South Florida | Michigan",
    bio: "Julian shares his pursuit of strength and muscle with a growing fitness community, bringing intensity, humor, and dedication to every workout. His focus on training and nutrition reflects The Farm To You's mission: quality American-raised protein to fuel big goals.",
    quote:
      "Quality American-raised protein to fuel big goals — that's exactly what I want powering my training.",
    badge: "Brand Ambassador",
    handle: "@juliandecina",
    photo: "/assets/julian-decina.jpg",
  },
  {
    id: "justin-mirra",
    name: "Justin Mirra",
    title: "Fitness Trainer & Performance Coach",
    location: "South Florida | New York · Post House Hamptons",
    bio: "Justin coaches high-performance clients across South Florida and New York, with summer programming out of Post House in the Hamptons. He builds protein-forward, clean-eating routines for athletes and busy professionals.",
    quote:
      "The Farm To You makes high-quality protein effortless — limited drops, real ranchers, and cuts I'm actually proud to cook for my clients.",
    badge: "Brand Ambassador",
    handle: "@Justin_mirra",
    photo: "/assets/justin-mirra.jpg",
  },
  {
    id: "cory-sims",
    name: "Cory Sims",
    title: "Athlete & Beast Games 2 Competitor",
    location: "United States",
    bio: "Cory brings a competitor's mindset to everything he cooks — training hard, eating clean, and sourcing the kind of protein that actually earns a place on his plate.",
    quote:
      "Real food, real ranchers. The Farm To You is what I reach for when the training block gets serious.",
    badge: "Brand Ambassador",
    handle: "@cory.sims191",
    photo: "/assets/cory-sims.jpg",
  },
  {
    id: "mia-spuler",
    name: "Mia Spuler",
    title: "Fitness Coach & Nutrition Specialist",
    location: "United States",
    bio: "Mia Spuler is an online fitness coach with a background in nutrition and dietetics, helping people build stronger bodies and healthier lifestyles. Her approach combines smart training, balanced nutrition, and sustainable habits to support long-term results. She believes that real progress starts with fueling your body right and staying consistent.",
    quote:
      "Real progress starts with fueling your body right and staying consistent.",
    badge: "Brand Ambassador",
    handle: "@miaspuler",
    photo: "/assets/mia-spuler.jpg",
    imgPos: "center top",
  },
  {
    id: "mira-cassano",
    name: "Mira Cassano",
    title: "Fitness Athlete & Lifestyle Creator",
    location: "United States",
    bio: "Mira Cassano combines her passion for fitness, an active lifestyle, and everyday wellness. Through her content, she shares glimpses of her training, food finds, and daily routines, inspiring her community to stay active and make choices that support their goals. Her focus on fitness and lifestyle aligns with The Farm To You's mission to make quality food part of a healthy, balanced life.",
    quote:
      "Quality food is part of a healthy, balanced life.",
    badge: "Brand Ambassador",
    handle: "@mira_cassano",
    photo: "/assets/mira-cassano.jpg",
  },
];

function PartnerCard({ partner }) {
  const badge = BADGE_STYLES[partner.badge] || BADGE_STYLES["Approved Partner"];
  const BadgeIcon = badge.icon;
  return (
    <article
      data-testid={`partner-card-${partner.id}`}
      className="snap-start shrink-0 w-[320px] sm:w-[360px] bg-white rounded-3xl border border-[var(--ff-border)] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_50px_-22px_rgba(26,67,49,0.35)] hover:border-[var(--ff-primary)]/30"
    >
      {/* Photo + badge */}
      <div className="relative h-72 overflow-hidden">
        <img
          src={partner.photo}
          alt={`${partner.name} — ${partner.title}`}
          loading="lazy"
          style={{ objectPosition: partner.imgPos || "center" }}
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
        <span
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold shadow-[0_8px_24px_-12px_rgba(0,0,0,0.55)] backdrop-blur-md ring-1 ring-white/25 tracking-wide"
          style={{ background: badge.bg, color: badge.fg }}
          data-testid={`partner-badge-${partner.id}`}
        >
          <BadgeIcon size={12} />
          {partner.badge}
        </span>
        <div className="absolute bottom-4 left-5 right-5 text-white">
          <h3 className="font-display text-2xl leading-tight">{partner.name}</h3>
          <p className="text-xs uppercase tracking-[0.2em] mt-1 text-white/80">
            {partner.title}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        {partner.location && (
          <p className="flex items-center gap-1.5 text-xs text-[var(--ff-text-muted)]">
            <MapPin size={12} />
            {partner.location}
          </p>
        )}
        <p className="mt-3 text-sm text-[var(--ff-text-secondary)] leading-relaxed">
          {partner.bio}
        </p>

        <div className="mt-5 relative pl-5 border-l-2 border-[var(--ff-citrus)]">
          <Quote
            size={14}
            className="absolute -left-[8px] top-0 bg-white text-[var(--ff-citrus)]"
          />
          <p className="text-sm italic text-[var(--ff-text-primary)] leading-relaxed">
            “{partner.quote}”
          </p>
        </div>

        {partner.handle && (
          <p className="mt-5 text-xs font-semibold tracking-wide text-[var(--ff-secondary)]">
            {partner.handle}
          </p>
        )}
      </div>
    </article>
  );
}

export default function TestedTastedLoved() {
  const scrollerRef = useRef(null);
  const [extra, setExtra] = useState([]);

  useEffect(() => {
    api.get("/partners/cards")
      .then((r) => setExtra(r.data?.cards || []))
      .catch(() => {});
  }, []);

  const partners = [...PARTNERS, ...extra];

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("article");
    const step = card ? card.offsetWidth + 24 : 360;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      data-testid="tested-tasted-loved"
      className="relative py-20 lg:py-28"
      style={{
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #FAF7F1 55%, #F3EFE6 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header + nav */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="overline text-[var(--ff-secondary)]">
              Tastemakers & partners
            </p>
            <h2
              className="mt-3 font-display leading-[1.05] text-[var(--ff-text-primary)]"
              style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.25rem)" }}
              data-testid="ttl-heading"
            >
              Tested, Tasted,<br />and Loved.
            </h2>
            <p className="mt-5 text-base lg:text-lg text-[var(--ff-text-secondary)] leading-relaxed">
              See why creators, athletes, wellness leaders, and everyday
              families are choosing The Farm To You for cleaner, better,
              farm-to-door food.
            </p>
          </div>

          {/* Desktop scroll controls */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="ttl-prev"
              aria-label="Previous partners"
              className="w-11 h-11 rounded-full border border-[var(--ff-border)] bg-white flex items-center justify-center text-[var(--ff-primary)] hover:bg-[var(--ff-primary)] hover:text-white hover:border-[var(--ff-primary)] transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="ttl-next"
              aria-label="Next partners"
              className="w-11 h-11 rounded-full border border-[var(--ff-border)] bg-white flex items-center justify-center text-[var(--ff-primary)] hover:bg-[var(--ff-primary)] hover:text-white hover:border-[var(--ff-primary)] transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative mt-12">
          {/* edge fade */}
          <div
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 hidden md:block"
            style={{
              background:
                "linear-gradient(90deg, rgba(250,247,241,0) 0%, rgba(250,247,241,1) 100%)",
            }}
          />
          <div
            ref={scrollerRef}
            data-testid="ttl-scroller"
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 -mx-6 px-6 scroll-smooth"
            style={{ scrollbarWidth: "thin" }}
          >
            {partners.map((p) => (
              <PartnerCard key={p.id} partner={p} />
            ))}
          </div>
        </div>

        {/* Partner CTA */}
        <div
          className="mt-16 lg:mt-20 rounded-3xl p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center"
          style={{
            background: "var(--ff-primary)",
            boxShadow: "0 30px 80px -45px rgba(26,67,49,0.6)",
          }}
          data-testid="partner-cta"
        >
          <div className="lg:col-span-8">
            <p
              className="overline"
              style={{ color: "#F4A261" }}
            >
              Partner with us
            </p>
            <h3 className="mt-3 font-display text-3xl lg:text-4xl text-white leading-tight">
              Want to Partner With The Farm To You?
            </h3>
            <p className="mt-4 text-white/80 leading-relaxed max-w-2xl">
              We partner with creators, athletes, chefs, wellness leaders, and
              local voices who believe in better food and supporting farmers
              across the country.
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:justify-end">
            <Link
              to="/ambassadors"
              data-testid="partner-cta-button"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-[var(--ff-primary)] transition-all hover:-translate-y-0.5"
              style={{
                background: "#F4A261",
                boxShadow: "0 18px 40px -18px rgba(244,162,97,0.7)",
              }}
            >
              Apply to Become a Brand Partner
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Compliance disclosure */}
        <p
          className="mt-8 max-w-3xl text-xs text-[var(--ff-text-muted)] leading-relaxed italic"
          data-testid="partner-disclosure"
        >
          Featured partners may receive compensation, complimentary product, or
          be part of a paid or gifted partnership with The Farm To You.
          Sponsored relationships are disclosed in line with FTC guidelines. We
          showcase partners for their taste, quality recommendations, cooking
          inspiration, and support of American farmers — no medical, health, or
          performance outcomes are claimed or implied.
        </p>
      </div>
    </section>
  );
}
